/**
 * What: Input contract for merchant votes on variation suggestions.
 * Transport / data-flow: Votes are reported as study-builder evaluation edits and saved Worker-side
 *   (`variation-suggestion-feedback-store.ts`) before any D1 write or PostHog capture; this schema is also used by the
 *   internal store-data devtool. Carries identifiers only — never the suggested text, baseline copy, product fields, or
 *   trace ID. Web rehydrates context from its ai_generation_batches and owns the trace lookup.
 */
import * as z from 'zod';
import { conceptDimensionSchema } from './product-research-inputs.js';
import { shopifyVariationSuggestionIntentSchema } from './shopify-treatment-field.js';
/** Votes available to merchants in the current feedback form. */
export const shopifyVariationFeedbackVoteSchema = z.enum(['up', 'down']);
export const shopifyVariationFeedbackPositiveReasonSchema = z.enum([
    'on_brand',
    'clearer_benefit',
    'useful_product_detail',
    'appealing_tone',
    'distinct_from_current',
]);
export const shopifyVariationFeedbackNegativeReasonSchema = z.enum([
    'off_brand',
    'repeats_baseline',
    'inaccurate_fact',
    'poor_tone',
    'not_usable',
    'unsafe',
]);
/** Existing persisted rows may contain the retired flag vote; retain it for read-only historical display. */
export const shopifyStoredVariationFeedbackVoteSchema = z.enum(['up', 'down', 'flag']);
export const shopifyVariationFeedbackReasonSchema = z.enum([
    ...shopifyVariationFeedbackNegativeReasonSchema.options,
    ...shopifyVariationFeedbackPositiveReasonSchema.options,
]);
export function isShopifyVariationFeedbackReasonForVote(vote, reason) {
    return (vote === 'up' ? shopifyVariationFeedbackPositiveReasonSchema : shopifyVariationFeedbackNegativeReasonSchema).safeParse(reason).success;
}
export const shopifyVariationFeedbackSchema = z
    .strictObject({
    logicalBatchId: z.string().trim().min(1).max(128),
    suggestionId: z.string().trim().min(1).max(80),
    conceptDimension: conceptDimensionSchema,
    suggestionIntent: shopifyVariationSuggestionIntentSchema.default('claim'),
    vote: shopifyVariationFeedbackVoteSchema,
    reasonCodes: z.array(shopifyVariationFeedbackReasonSchema).max(3).optional(),
    comment: z.string().trim().max(500).optional(),
    clientSubmissionId: z.string().uuid(),
})
    .superRefine((feedback, context) => {
    const reasons = feedback.reasonCodes ?? [];
    if (feedback.vote === 'up' && reasons.length === 0)
        context.addIssue({ code: 'custom', path: ['reasonCodes'], message: 'Choose at least one positive reason.' });
    reasons.forEach((reason, index) => {
        if (!isShopifyVariationFeedbackReasonForVote(feedback.vote, reason))
            context.addIssue({ code: 'custom', path: ['reasonCodes', index], message: 'Reason does not match the vote.' });
    });
});
//# sourceMappingURL=shopify-variation-feedback.js.map